/* eslint-disable no-console, consistent-return, @typescript-eslint/no-use-before-define */
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import {
  IEvent,
  IGameMessage,
  IGameState,
  IMessageBody,
  IMessageHeader,
  IQueueManager,
  TQueuedPlayFlowPlay,
  TQueueType,
} from '@/types/general';
import { IPlayflowPlay } from '@/types';
import { GAME_STATE_TIMEOUT_MS, sendBulkUpdate, sendUpdate } from '@/api/requests';
import { clone, omit, findIndex, last, cloneDeep } from 'lodash';
import { useToast } from 'vue-toastification';
import { useUserStore } from './user';

const toast = useToast();

export const PENDING_PLAYFLOW_KEY = 'pendingPlayflowPlays';
export const ACTIVE_PLAYFLOW_KEY = 'activePlayflowPlays';
export const PENDING_PLAYFLOW_PUTS_KEY = 'pendingPlayflowPuts';
export const GAMESTATE_ACTIVE_KEY = 'activeGameState';
export const GAMESTATE_PENDING_KEY = 'pendingGameState';
const MAX_RETRIES = 10;
const MAX_PLAY_BATCH_SIZE = 8;
const RETRY_DELAY = [5, 10, 15, 30];

const delay = (seconds: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, seconds * 1000);
  });

const messageHeader = () => {
  const dateTime = new Date();
  const { getUserRole } = useUserStore();
  const header: IMessageHeader = {
    messageType: 1,
    scout: getUserRole(),
    clientTimestamp: dateTime.toISOString(),
  };
  return header;
};

const sendPlayFlowUpdate = async (
  data: { gameId: number; play: IPlayflowPlay; clientTimestamp: string }[]
) => {
  const gameMessages: IGameMessage[] = data.map(({ clientTimestamp, ...obj }) => ({
    header: { ...messageHeader(), clientTimestamp },
    body: obj,
  }));
  return sendBulkUpdate(gameMessages);
};

const sendPlayFlowPut = async (data: { gameId: number; play: IPlayflowPlay; clientTimestamp: string }) => {
  const { gameId, play, clientTimestamp } = data;
  const body: IMessageBody = {
    gameId,
    play,
  };
  const gameMessage: IGameMessage = {
    header: { ...messageHeader(), clientTimestamp },
    body,
  };
  return sendUpdate(gameMessage, true, 'put');
};

const sendGameStateUpdate = async (
  data: { gameId: number; gameState: IGameState; events: IEvent[] | null; clientTimestamp: string }[]
) => {
  const { clientTimestamp, ...body } = data[0];
  const gameMessages: IGameMessage[] = [
    {
      header: { ...messageHeader(), clientTimestamp },
      body,
    },
  ];
  return sendBulkUpdate(gameMessages, GAME_STATE_TIMEOUT_MS);
};

export const useQueueManager = defineStore('queueManager', () => {
  const queue = ref<IQueueManager>({
    pendingGameStateUpdate: null,
    activeGameStateUpdate: null,
    isProcessingGameState: false,

    gameStateEvents: null,

    pendingPlayflowPuts: [],
    isProcessingPlayflowPuts: false,

    pendingPlayflowUpdate: [],
    activePlayflowUpdate: [],
    isProcessingPlays: false,
  });

  const hasDetectedErrors = ref<boolean>(false);
  const quarantinedPlayflowUpdates = ref<TQueuedPlayFlowPlay[]>([]);
  const quarantinedPlayflowPuts = ref<TQueuedPlayFlowPlay[]>([]);

  const persistPlayQueues = (): void => {
    localStorage.setItem(
      ACTIVE_PLAYFLOW_KEY,
      JSON.stringify([...quarantinedPlayflowUpdates.value, ...queue.value.activePlayflowUpdate])
    );
    localStorage.setItem(PENDING_PLAYFLOW_KEY, JSON.stringify(queue.value.pendingPlayflowUpdate));
    localStorage.setItem(
      PENDING_PLAYFLOW_PUTS_KEY,
      JSON.stringify([...quarantinedPlayflowPuts.value, ...queue.value.pendingPlayflowPuts])
    );
  };

  /**
   * Returns descending ordered plays with updates applied
   * to be prepended to game snapshot plays for initial page
   * loads with pending plays still in queue
   */
  const currentQueuePlaysForDisplay = computed((): IPlayflowPlay[] => {
    const combinedQueuedPlays = [...queue.value.activePlayflowUpdate, ...queue.value.pendingPlayflowUpdate];

    queue.value.pendingPlayflowPuts.forEach((update) => {
      const ndx = findIndex(
        combinedQueuedPlays,
        (play) => play.driveNumber === update.driveNumber && play.drivePlayNumber === update.drivePlayNumber
      );
      combinedQueuedPlays.splice(ndx, 1, update);
    });

    return combinedQueuedPlays;
  });

  const popPendingGameState = (): void => {
    queue.value.activeGameStateUpdate = queue.value.pendingGameStateUpdate;
    queue.value.pendingGameStateUpdate = null;
    queue.value.gameStateEvents = null;
    localStorage.setItem(GAMESTATE_ACTIVE_KEY, JSON.stringify(queue.value.activeGameStateUpdate));
    localStorage.setItem(GAMESTATE_PENDING_KEY, JSON.stringify(queue.value.pendingGameStateUpdate));
  };

  const popPendingPlayState = (): void => {
    queue.value.activePlayflowUpdate.push(...queue.value.pendingPlayflowUpdate);
    queue.value.pendingPlayflowUpdate = [];
    persistPlayQueues();
  };

  const popPendingPlayPuts = (): void => {
    queue.value.pendingPlayflowPuts.shift();
    persistPlayQueues();
  };

  const dropDuplicateQueuedPlays = (gameId: number, snapshotPlays: IPlayflowPlay[]): void => {
    const snapshotPlayKeys = new Set(
      snapshotPlays.map((play) => `${play.driveNumber}-${play.drivePlayNumber}`)
    );
    const removeSnapshotMatches = <TPlay extends IPlayflowPlay & { gameId: number }>(
      plays: TPlay[]
    ): TPlay[] =>
      plays.filter((play) => {
        const isDuplicate =
          play.gameId === gameId && snapshotPlayKeys.has(`${play.driveNumber}-${play.drivePlayNumber}`);

        if (isDuplicate) {
          console.log(`Dropping queued duplicate play ${play.driveNumber}-${play.drivePlayNumber}`);
        }

        return !isDuplicate;
      });

    quarantinedPlayflowUpdates.value = removeSnapshotMatches(quarantinedPlayflowUpdates.value);
    quarantinedPlayflowPuts.value = removeSnapshotMatches(quarantinedPlayflowPuts.value);
    persistPlayQueues();
  };

  const clearQueue = (type: TQueueType): void => {
    if (type === TQueueType.PLAY || type === TQueueType.PLAY_PUT) {
      queue.value.pendingPlayflowPuts = [];
      queue.value.activePlayflowUpdate = [];
      queue.value.pendingPlayflowUpdate = [];
      quarantinedPlayflowUpdates.value = [];
      quarantinedPlayflowPuts.value = [];
      localStorage.removeItem(PENDING_PLAYFLOW_KEY);
      localStorage.removeItem(ACTIVE_PLAYFLOW_KEY);
      localStorage.removeItem(PENDING_PLAYFLOW_PUTS_KEY);
    } else {
      queue.value.pendingGameStateUpdate = null;
      queue.value.activeGameStateUpdate = null;
      localStorage.removeItem(GAMESTATE_ACTIVE_KEY);
      localStorage.removeItem(GAMESTATE_PENDING_KEY);
    }
  };

  // isAuthFailure=true means the request resolved (didn't throw) but was rejected due to auth (401/403/435) -
  // this must NOT be treated as a successful send, or the item is silently dropped from the queue.
  const handleRequestFailure = async (
    type: TQueueType,
    retries: number,
    isAuthFailure = false
  ): Promise<void> => {
    if (!hasDetectedErrors.value) {
      hasDetectedErrors.value = true;
      const message = isAuthFailure
        ? 'Reconnecting, will retry automatically'
        : 'Limited connectivity! Keep collecting data. Banjo will retry in the background';
      toast.warning(message, {
        position: 'bottom-center',
        timeout: 6000,
      });
    }

    queue.value.gameStateEvents = null;

    if (retries >= 0) {
      console.log(
        `Try ${MAX_RETRIES - retries} failed, trying again in ${
          RETRY_DELAY[MAX_RETRIES - retries] || last(RETRY_DELAY)
        } secs`
      );
      await delay(RETRY_DELAY[MAX_RETRIES - retries] || last(RETRY_DELAY));

      // if retrying check if there is pending data to add into the active set
      if (type === TQueueType.GAME && !!queue.value.pendingGameStateUpdate) {
        popPendingGameState();
      } else if (type === TQueueType.PLAY && queue.value.pendingPlayflowUpdate.length > 0) {
        popPendingPlayState();
      }

      return requestWithRetry(type, retries - 1);
    }

    hasDetectedErrors.value = false;

    if (type === TQueueType.GAME) {
      queue.value.isProcessingGameState = false;
    } else if (type === TQueueType.PLAY) {
      queue.value.isProcessingPlays = false;
    } else {
      queue.value.isProcessingPlayflowPuts = false;
    }

    toast.error('Data loss! Please text SIS immediately, then resume data collection', {
      position: 'bottom-center',
      timeout: 6000,
    });
    console.error('Retries exhausted, removing play from queue');

    clearQueue(type);
    return undefined;
  };

  const requestWithRetry = async (type: TQueueType, retries = MAX_RETRIES): Promise<void> => {
    let requestFunc: Function;
    let reqData = {};

    if (type === TQueueType.PLAY) {
      queue.value.isProcessingPlays = true;

      const plays = cloneDeep(queue.value.activePlayflowUpdate.slice(0, MAX_PLAY_BATCH_SIZE));
      requestFunc = sendPlayFlowUpdate;
      reqData = plays.map((play) => ({
        play: omit(play, ['gameId', 'clientTimestamp']),
        gameId: play.gameId,
        clientTimestamp: play.clientTimestamp,
      }));
    } else if (type === TQueueType.PLAY_PUT) {
      queue.value.isProcessingPlayflowPuts = true;

      const play = clone(queue.value.pendingPlayflowPuts[0]);
      requestFunc = sendPlayFlowPut;
      reqData = {
        play: omit(play, ['gameId', 'clientTimestamp']),
        gameId: play.gameId,
        clientTimestamp: play.clientTimestamp,
      };
    } else {
      queue.value.isProcessingGameState = true;

      const gameState = clone(queue.value.activeGameStateUpdate);
      requestFunc = sendGameStateUpdate;
      reqData = [
        {
          gameState: omit(gameState, ['gameId', 'clientTimestamp']),
          gameId: gameState.gameId,
          events: queue.value.gameStateEvents,
          clientTimestamp: gameState.clientTimestamp,
        },
      ];
    }

    try {
      const result = await requestFunc(reqData);
      // sendUpdate/sendBulkUpdate resolve to `false` (rather than throwing) on an auth failure -
      // treating that as success would silently drop the item from the queue.
      if (result === false) {
        return await handleRequestFailure(type, retries, true);
      }
      afterRequestHandler(type);
      return undefined;
    } catch (e) {
      return handleRequestFailure(type, retries, false);
    }
  };

  const afterRequestHandler = async (type: TQueueType) => {
    if (type === TQueueType.GAME) {
      popPendingGameState();
    } else if (type === TQueueType.PLAY) {
      queue.value.activePlayflowUpdate.splice(0, MAX_PLAY_BATCH_SIZE);
      popPendingPlayState();
    } else {
      popPendingPlayPuts();
    }

    if (type === TQueueType.PLAY && queue.value.activePlayflowUpdate.length > 0) {
      // continue on to processing next play if neccessary
      return requestWithRetry(TQueueType.PLAY);
    }
    if (type === TQueueType.PLAY) {
      queue.value.isProcessingPlays = false;

      if (queue.value.pendingPlayflowPuts.length) {
        return requestWithRetry(TQueueType.PLAY_PUT);
      }
    } else if (type === TQueueType.GAME && queue.value.activeGameStateUpdate) {
      // continue on to processing next gamestate update if neccessary
      return requestWithRetry(TQueueType.GAME);
    } else if (type === TQueueType.GAME) {
      queue.value.isProcessingGameState = false;
    } else {
      queue.value.isProcessingPlayflowPuts = false;

      if (queue.value.activePlayflowUpdate.length === 0 && queue.value.pendingPlayflowPuts.length) {
        return requestWithRetry(TQueueType.PLAY_PUT);
      }
      if (queue.value.activePlayflowUpdate.length) {
        return requestWithRetry(TQueueType.PLAY);
      }
    }
  };

  const init = (): void => {
    const cachedPendingPlays = localStorage.getItem(PENDING_PLAYFLOW_KEY);
    const cachedActivePlays = localStorage.getItem(ACTIVE_PLAYFLOW_KEY);
    const cachedPendingPlayPuts = localStorage.getItem(PENDING_PLAYFLOW_PUTS_KEY);
    const cachedActiveGameState = localStorage.getItem(GAMESTATE_ACTIVE_KEY);
    const cachedPendingGameState = localStorage.getItem(GAMESTATE_PENDING_KEY);

    queue.value.activeGameStateUpdate = JSON.parse(cachedActiveGameState);
    queue.value.pendingGameStateUpdate = JSON.parse(cachedPendingGameState);
    quarantinedPlayflowUpdates.value = [
      ...(JSON.parse(cachedActivePlays) || []),
      ...(JSON.parse(cachedPendingPlays) || []),
    ];
    quarantinedPlayflowPuts.value = JSON.parse(cachedPendingPlayPuts) || [];
    queue.value.pendingPlayflowUpdate = [];
    queue.value.activePlayflowUpdate = [];
    queue.value.pendingPlayflowPuts = [];
    persistPlayQueues();

    if (queue.value.activeGameStateUpdate) {
      requestWithRetry(TQueueType.GAME);
    }
  };

  const resumeQueuedPlayProcessing = (): void => {
    queue.value.pendingPlayflowUpdate.push(...quarantinedPlayflowUpdates.value);
    queue.value.pendingPlayflowPuts.push(...quarantinedPlayflowPuts.value);
    quarantinedPlayflowUpdates.value = [];
    quarantinedPlayflowPuts.value = [];
    persistPlayQueues();

    if (queue.value.isProcessingPlays || queue.value.isProcessingPlayflowPuts) {
      return;
    }

    if (!queue.value.activePlayflowUpdate.length && queue.value.pendingPlayflowUpdate.length) {
      popPendingPlayState();
    }

    if (queue.value.activePlayflowUpdate.length) {
      requestWithRetry(TQueueType.PLAY);
    } else if (queue.value.pendingPlayflowPuts.length) {
      requestWithRetry(TQueueType.PLAY_PUT);
    }
  };

  const enqueuePlay = async (gameId: number, play: IPlayflowPlay, isUpdate = false): Promise<void> => {
    const clientTimestamp = new Date().toISOString();

    if (!queue.value.isProcessingPlays && !isUpdate) {
      queue.value.activePlayflowUpdate.push({ ...play, gameId, clientTimestamp });
      persistPlayQueues();

      requestWithRetry(TQueueType.PLAY);
    } else if (!isUpdate) {
      queue.value.pendingPlayflowUpdate.push({ ...play, gameId, clientTimestamp });
      persistPlayQueues();
    } else if (isUpdate && !queue.value.isProcessingPlays && !queue.value.isProcessingPlayflowPuts) {
      queue.value.pendingPlayflowPuts.push({ ...play, gameId, clientTimestamp });
      persistPlayQueues();

      requestWithRetry(TQueueType.PLAY_PUT);
    } else {
      queue.value.pendingPlayflowPuts.push({ ...play, gameId, clientTimestamp });
      persistPlayQueues();
    }
  };

  const enqueueGameStateUpdate = async (
    gameId: number,
    gameState: IGameState,
    events?: IEvent[]
  ): Promise<void> => {
    queue.value.gameStateEvents = events ?? null;
    const clientTimestamp = new Date().toISOString();

    if (!queue.value.isProcessingGameState) {
      queue.value.activeGameStateUpdate = { ...gameState, gameId, clientTimestamp };
      localStorage.setItem(GAMESTATE_ACTIVE_KEY, JSON.stringify(queue.value.activeGameStateUpdate));
      requestWithRetry(TQueueType.GAME);
    } else {
      queue.value.pendingGameStateUpdate = { ...gameState, gameId, clientTimestamp };
      localStorage.setItem(GAMESTATE_PENDING_KEY, JSON.stringify(queue.value.pendingGameStateUpdate));
    }
  };

  return {
    queue,
    init,
    enqueuePlay,
    enqueueGameStateUpdate,
    clearQueue,
    popPendingGameState,
    popPendingPlayState,
    popPendingPlayPuts,
    dropDuplicateQueuedPlays,
    resumeQueuedPlayProcessing,
    currentQueuePlaysForDisplay,
  };
});
